import React, { useEffect, useState } from 'react';
import { getClinicLogoBlobUrl } from '../lib/clinicLogo';

/**
 * PrintHeader — the canonical printable-document letterhead.
 *
 * Layout: logo on the top-left (72 px square, contain-fitted), clinic
 * name + address block to the right of the logo, and an optional
 * per-document title / number / date column right-aligned on the far
 * right. Used by every A4/A5 printable in AUDINEXA — invoices,
 * challans, tokens, receipts, reports — so branding is consistent
 * across every piece of paper a patient walks out with.
 *
 * `variant="a4"` (default) is the full 3-column layout described
 * above; `variant="thermal"` is a centered, stacked layout tuned for
 * 80 mm thermal receipts.
 */
export default function PrintHeader({
  clinic,
  docTitle,
  docNo,
  docDate,
  docSubtitle,
  statusPill,
  variant = 'a4',
}) {
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!clinic?.logo_fs_id) return;
    getClinicLogoBlobUrl().then((u) => { if (alive) setLogoUrl(u); });
    return () => { alive = false; };
  }, [clinic?.logo_fs_id]);

  if (variant === 'thermal') {
    return (
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt="clinic logo"
            data-testid="print-header-logo"
            style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain', margin: '0 auto 4px' }}
          />
        )}
        <div style={{ fontWeight: 700, fontSize: 12 }}>
          {clinic?.name || 'AUDINEXA Audiology Clinic'}
        </div>
        {clinic?.address && <div style={{ fontSize: 10 }}>{clinic.address}</div>}
        {(clinic?.city || clinic?.state) && (
          <div style={{ fontSize: 10 }}>
            {[clinic?.city, clinic?.state, clinic?.pincode].filter(Boolean).join(', ')}
          </div>
        )}
        {clinic?.phone && <div style={{ fontSize: 10 }}>Ph: {clinic.phone}</div>}
        {clinic?.email && <div style={{ fontSize: 10 }}>{clinic.email}</div>}
        {clinic?.gstin && <div style={{ fontSize: 10 }}>GSTIN: {clinic.gstin}</div>}
      </div>
    );
  }

  // A4 layout — logo + address block, with optional right column.
  return (
    <div
      className="flex flex-col sm:flex-row print:flex-row items-start justify-between gap-3 border-b-2 border-slate-800 pb-3 mb-4"
      data-testid="print-header"
    >
      <div className="flex items-start gap-3 min-w-0">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="clinic logo"
            data-testid="print-header-logo"
            className="w-14 h-14 sm:w-[72px] sm:h-[72px] print:w-[72px] print:h-[72px] object-contain shrink-0 border border-slate-100 rounded bg-white"
            crossOrigin="anonymous"
          />
        ) : null}
        <div className="min-w-0">
          <div className="text-xl sm:text-2xl print:text-2xl font-bold text-slate-800 leading-tight break-words">
            {clinic?.name || 'AUDINEXA Audiology Clinic'}
          </div>
          <div className="text-[11px] text-slate-600 leading-tight mt-0.5">
            {clinic?.address && <div>{clinic.address}</div>}
            {(clinic?.city || clinic?.state) && (
              <div>{[clinic?.city, clinic?.state, clinic?.pincode].filter(Boolean).join(', ')}</div>
            )}
            {clinic?.phone && <div>Phone: {clinic.phone}</div>}
            {clinic?.email && <div>Email: {clinic.email}</div>}
            {clinic?.gstin && <div className="font-mono">GSTIN: {clinic.gstin}</div>}
          </div>
        </div>
      </div>

      {(docTitle || docNo || docDate || statusPill) && (
        <div className="sm:text-right print:text-right shrink-0 sm:ml-4 print:ml-4">
          {docTitle && (
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
              {docTitle}
            </div>
          )}
          {docNo && (
            <div className="text-lg font-bold text-slate-800 font-mono" data-testid="print-header-docno">
              {docNo}
            </div>
          )}
          {docDate && <div className="text-[11px] text-slate-600 mt-0.5">Date: {docDate}</div>}
          {docSubtitle && <div className="text-[10px] text-slate-500 mt-0.5">{docSubtitle}</div>}
          {statusPill && <div className="print:hidden mt-1">{statusPill}</div>}
        </div>
      )}
    </div>
  );
}
